import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';
import { UserControllerService } from '@app/generated/service';
import { CompanyDto } from '@app/generated/model';
import { UserService } from '@app/core/services/user.service';

export interface CompanySelectDialogData {
  projectId: string;
  projectName?: string;
  selectedCompanyId?: string;
}

@Component({
  selector: 'app-company-select-dialog',
  templateUrl: './company-select-dialog.component.html',
  styleUrls: ['./company-select-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanySelectDialogComponent {
  public companies: CompanyDto[] = [];
  public loading = true;
  public saving = false;
  public selectedCompanyId = '';
  public readonly projectName?: string;

  private readonly projectId: string;

  constructor(
    private readonly dialogRef: MatDialogRef<CompanySelectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: CompanySelectDialogData,
    private readonly userController: UserControllerService,
    private readonly userService: UserService,
  ) {
    this.projectId = data.projectId;
    this.projectName = data.projectName;
    this.selectedCompanyId = data.selectedCompanyId ?? '';
    this.loadCompanies();
  }

  canConfirm(): boolean {
    return !!this.selectedCompanyId && !this.saving;
  }

  async confirm(): Promise<void> {
    const company = this.companies.find((c) => c.id === this.selectedCompanyId);
    if (!company?.id) {
      return;
    }

    this.saving = true;
    try {
      await lastValueFrom(this.userController.setSelectedCompany(this.projectId, { companyId: company.id }));
      await this.userService.load();
      this.dialogRef.close(company);
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private loadCompanies(): void {
    this.userController
      .getProjectCompanies(this.projectId)
      .pipe(first())
      .subscribe({
        next: (companies) => {
          this.companies = companies ?? [];
          if (!this.selectedCompanyId && this.companies.length === 1) {
            this.selectedCompanyId = this.companies[0].id ?? '';
          }
          this.loading = false;
        },
        error: () => {
          this.companies = [];
          this.loading = false;
        },
      });
  }
}
